import React, { useState, useEffect, useCallback } from 'react';
import {
  BeakerIcon,
  PlusIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, Spinner, Modal, SearchInput, Badge } from '../../components/ui';
import { sysadminService } from '../../services/sysadmin.service';
import type { Pharmacy } from '../../types';
import { toast } from 'react-toastify';

interface Dosage {
  id?: string;
  dosage: string;
  form: string;
  dispenseUnitId?: string;
}

interface Compound {
  id: string;
  name: string;
  questionnaireId?: string;
  pharmacyId?: string | null;
  pharmacy?: { id: string; name: string; code: string } | null;
  dosageVarieties?: Dosage[];
  dosages?: Dosage[];
}

export const Medications: React.FC = () => {
  const [compounds, setCompounds] = useState<Compound[]>([]);
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompound, setSelectedCompound] = useState<Compound | null>(null);
  const [form, setForm] = useState<{ name: string; questionnaireId: string; pharmacyId: string }>({ name: '', questionnaireId: '', pharmacyId: '' });
  const [dosages, setDosages] = useState<Dosage[]>([{ dosage: '', form: '' }]);

  const fetchCompounds = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await sysadminService.getCompounds();
      setCompounds(Array.isArray(data) ? data : data?.data || []);
    } catch {
      toast.error('Failed to load medications');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPharmacies = useCallback(async () => {
    try {
      const data = await sysadminService.getPharmacies();
      setPharmacies(Array.isArray(data) ? data.filter((p) => p.isActive) : []);
    } catch {
      // Non-fatal: medication editing still works without the pharmacy list.
    }
  }, []);

  useEffect(() => {
    fetchCompounds();
    fetchPharmacies();
  }, [fetchCompounds, fetchPharmacies]);

  const filteredCompounds = compounds.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setSelectedCompound(null);
    setForm({ name: '', questionnaireId: '', pharmacyId: '' });
    setDosages([{ dosage: '', form: '' }]);
    setIsModalOpen(true);
  };

  const handleEdit = (compound: Compound) => {
    setSelectedCompound(compound);
    setForm({ name: compound.name, questionnaireId: compound.questionnaireId || '', pharmacyId: compound.pharmacyId || '' });
    const existingDosages = compound.dosageVarieties || compound.dosages || [];
    setDosages(existingDosages.length > 0 ? existingDosages.map((d) => ({ ...d })) : [{ dosage: '', form: '' }]);
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      questionnaireId: form.questionnaireId || undefined,
      pharmacyId: form.pharmacyId || null,
      dosages: dosages.filter((d) => d.dosage && d.form),
    };

    try {
      if (selectedCompound) {
        await sysadminService.updateCompound(selectedCompound.id, payload);
        toast.success('Medication updated');
      } else {
        await sysadminService.createCompound(payload);
        toast.success('Medication created');
      }
      setIsModalOpen(false);
      fetchCompounds();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save medication');
    }
  };

  const addDosage = () => {
    setDosages([...dosages, { dosage: '', form: '' }]);
  };

  const removeDosage = (index: number) => {
    setDosages(dosages.filter((_, i) => i !== index));
  };

  const updateDosage = (index: number, field: keyof Dosage, value: string) => {
    setDosages(dosages.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50">
            Medications
          </h1>
          <p className="text-secondary-500 dark:text-secondary-400">
            Manage platform medication catalog
          </p>
        </div>
        <Button onClick={handleAdd}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Medication
        </Button>
      </div>

      {/* Content */}
      <Card>
        <div className="p-6">
          <div className="mb-6">
            <SearchInput
              placeholder="Search medications..."
              value={searchTerm}
              onChange={setSearchTerm}
            />
          </div>

          {filteredCompounds.length === 0 ? (
            <div className="text-center py-12">
              <BeakerIcon className="mx-auto h-12 w-12 text-secondary-400" />
              <h3 className="mt-2 text-sm font-medium text-secondary-900 dark:text-secondary-50">No medications found</h3>
              <p className="mt-1 text-sm text-secondary-500">
                {searchTerm ? 'Try adjusting your search.' : 'Get started by adding a medication.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200 dark:border-secondary-700">
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Pharmacy</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Available Dosages</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
                  {filteredCompounds.map((compound) => {
                    const allDosages = compound.dosageVarieties || compound.dosages || [];
                    return (
                      <tr key={compound.id} className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                        <td className="py-3 px-4">
                          <span className="text-sm font-medium text-secondary-900 dark:text-secondary-50">{compound.name}</span>
                        </td>
                        <td className="py-3 px-4">
                          {(() => {
                            const pharmacyName =
                              compound.pharmacy?.name ||
                              pharmacies.find((p) => p.id === compound.pharmacyId)?.name;
                            return pharmacyName ? (
                              <Badge variant="secondary" size="sm">{pharmacyName}</Badge>
                            ) : (
                              <span className="text-sm text-secondary-400">Curexa (default)</span>
                            );
                          })()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1.5">
                            {allDosages.map((d, i) => (
                              <Badge key={d.id || i} variant="primary" size="sm">
                                {d.dosage} ({d.form})
                              </Badge>
                            ))}
                            {allDosages.length === 0 && (
                              <span className="text-sm text-secondary-400 italic">No dosages</span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="secondary" size="sm" onClick={() => handleEdit(compound)}>
                              <PencilIcon className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedCompound ? 'Edit Medication' : 'Add Medication'}
        size="lg"
      >
        <div className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Fulfillment pharmacy */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">Fulfillment Pharmacy</label>
            <select
              value={form.pharmacyId}
              onChange={(e) => setForm({ ...form, pharmacyId: e.target.value })}
              className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Default (Curexa)</option>
              {pharmacies.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-secondary-500">
              Orders for this medication are routed to the selected pharmacy. Offerings using PCP Health also need a Pharmacy Medication ID set on each product.
            </p>
          </div>

          {/* Dosages */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">Dosages</label>
              <button
                type="button"
                onClick={addDosage}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium"
              >
                + Add dosage
              </button>
            </div>
            <div className="space-y-2">
              {dosages.map((dosage, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Dosage (e.g. 500mg)"
                    value={dosage.dosage}
                    onChange={(e) => updateDosage(index, 'dosage', e.target.value)}
                    className="flex-1 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  <input
                    type="text"
                    placeholder="Form (e.g. tablet)"
                    value={dosage.form}
                    onChange={(e) => updateDosage(index, 'form', e.target.value)}
                    className="flex-1 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-secondary-50 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                  {dosages.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeDosage(index)}
                      className="p-2 text-secondary-400 hover:text-danger-500"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>{selectedCompound ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Medications;
